import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import VuiBox from "components/ui/VuiBox";
import BarChart from "components/charts/BarChart";
import ChartInfoTooltip from "components/Tooltip/ChartInfoTooltip";
import { getStackedBarChartOptions } from "../../data/barChartOptions";
import { chartTooltips } from "components/Tooltip/Definitions/data";
import axiosInstance from "services/httpInterceptor";
import StandardLoadingIndicator from "components/StandardLoadingIndicator";
import StandardMessageDisplay from "components/StandardMessageDisplay";
import GlassCard from "components/GlassCard";
import { CHART_COLORS } from "utils/designTokens";

function ContentCategoryDistribution({ companyId, chartType }) {

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState({ categories: [], series: [] });

  useEffect(() => {
    const fetchCategoryDistribution = async () => {
      if (!companyId) {
        setLoading(false);
        setError("Missing required parameters.");
        setChartData({ categories: [], series: [] });
        return;
      }

      setLoading(true);
      setError(null);
      try {
        let endpoint;
        if (chartType === "postType") {
          endpoint = `/v1/frontend/content/category-distribution`;
        } else if (chartType === "writingTone") {
          endpoint = `/v1/frontend/content/style-distribution`;
        } else {
          throw new Error(`Chart type ${chartType} is not supported`);
        }

        const params = { companyId };

        const response = await axiosInstance.get(endpoint, { params });
        const newData = response.data || { categories: [], series: [] };

        setChartData(currentData =>
          JSON.stringify(currentData) !== JSON.stringify(newData) ? newData : currentData,
        );
      } catch (err) {
        console.error("Error fetching content category distribution:", err);
        setError("Failed to load content category distribution data.");
        setChartData({ categories: [], series: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchCategoryDistribution();
  }, [companyId, chartType]);

  // Memoize chart options to prevent unnecessary recalculations
  const chartOptions = useMemo(() => {
    const yAxisTitle =
      chartType === "postType"
        ? "Published Content Categories (%)"
        : "Marketing Objectives (%)";
    const baseOptions = getStackedBarChartOptions(chartData.categories, yAxisTitle);

    // Override colors with our enhanced palette
    return {
      ...baseOptions,
      colors: CHART_COLORS,
      legend: {
        ...baseOptions.legend,
        markers: {
          ...baseOptions.legend.markers,
          fillColors: CHART_COLORS,
        },
      },
      yaxis: {
        ...baseOptions.yaxis,
        max: 100,
        min: 0,
        labels: {
          ...baseOptions.yaxis.labels,
          formatter: function(value) {
            return value + "%";
          },
        },
      },
    };
  }, [chartData.categories, chartType]);

  // Prepare data for the chart - memoized to prevent unnecessary processing
  const preparedChartData = useMemo(() => {
    if (!chartData.series || chartData.series.length === 0) {
      return [];
    }

    return chartData.series.map((series, index) => ({
      name: series.name,
      data: series.data,
      color: CHART_COLORS[index % CHART_COLORS.length], // Use modulo to cycle through colors if more categories than colors
    }));
  }, [chartData.series]);

  const getChartTitle = () => {
    return chartType === "postType" ? "Competitors Published Content Categories" : "Competitors Marketing Strategy Objectives";
  };

  const getChartTooltipKey = () => {
    if (chartType === "postType") {
      return "contentCategoryDistribution";
    } else {
      return "communicationStyleComparison";
    }
  };

  return (
    <GlassCard>
      <VuiBox sx={{ height: "100%" }}>
        <ChartInfoTooltip
          title={getChartTitle()}
          tooltipText={chartTooltips[getChartTooltipKey()]}
        >
          <VuiBox sx={{ position: "relative", minHeight: "400px", paddingBottom: "10px" }}>
            {loading ? (
              <StandardLoadingIndicator containerHeight="328px" />
            ) : error ? (
              <StandardMessageDisplay
                type="error"
                containerHeight="368px"
              />
            ) : chartData.categories.length === 0 && chartData.series.length === 0 ? (
              <StandardMessageDisplay
                type="noData"
                title={getChartTitle() + " - No Data"}
                message={`No data available for ${getChartTitle().toLowerCase()} for the selected criteria.`}
                containerHeight="368px"
              />
            ) : (
              <BarChart
                barChartData={preparedChartData}
                barChartOptions={chartOptions}
              />
            )}
          </VuiBox>
        </ChartInfoTooltip>
      </VuiBox>
    </GlassCard>
  );
}

ContentCategoryDistribution.propTypes = {
  companyId: PropTypes.string.isRequired,
  chartType: PropTypes.string.isRequired,
};

export default ContentCategoryDistribution;